import { useModalityCategoryValue } from "@FillInTableContainer/shared/context/ModalityCategoryContext";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  ValidatedInfoList,
  useValidatedInTableCreator,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useEntryDataValue } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

interface UseCheckValidationInTableCreatorReturn {
  handleCheckValidationFull: () => boolean;
  handleCheckValidationHalf: () => boolean;
}

export const useCheckValidationInRegulatoryTableCreator =
  (): UseCheckValidationInTableCreatorReturn => {
    const entryData = useEntryDataValue(getPressIdFromURL());
    const { changeValidatedInfo } = useValidatedInTableCreator();
    const { changeInfo } = useTableCreatorPositionInfo();
    const { changeFocusStatus } = useTableCreatorFocusStatus();

    const { updateModalityCategory } = useModalityCategoryValue();

    const checkModalityCategory = () => {
      const drugProfileTableList = entryData?.Regulatory?.["Drug Profile"];
      const modalityValues = drugProfileTableList?.map((table) =>
        table.Modality.value?.map((shell) => shell.text),
      );

      const valueInfo = modalityValues?.flatMap((table, tableIndex) =>
        table.map((shell) => {
          if (updateModalityCategory[shell] === "") {
            return {
              groupTableKey: "Drug Profile",
              groupTableIndex: 0,
              tableKey: "Drug",
              tableIndex,
              groupShellKey: null,
              groupShellIndex: 0,
              shellKey: "Modality",
              shellIndex: 0,
              valueIndex: null,
              type: "alert",
              container: "shell",
            };
          }
          return null;
        }),
      );
      return valueInfo?.filter((item) => item !== null);
    };

    const checkApprovalProcessTable = () => {
      const approvalProcessData =
        entryData.Regulatory?.["Approval Process Contents"];

      const sortedValidatedInfo = () => {
        const validatedInfoTemp: ValidatedInfoList = [];

        approvalProcessData?.["Approval Process"]?.forEach(
          (groupShellInfo, groupShellInfoIndex) => {
            const isValidatedAuthorizedCompany =
              groupShellInfo["Authorized Company"].value.length === 0;

            const isValidatedRegulatoryAgency =
              groupShellInfo["Regulatory Agency"].value.length === 0;

            const isValidatedApplicationType =
              groupShellInfo["Application Type"].value.length === 0;

            const isValidatedMarketingStatus =
              groupShellInfo["Marketing Status"].value.length === 0;

            if (
              isValidatedApplicationType ||
              isValidatedAuthorizedCompany ||
              isValidatedMarketingStatus ||
              isValidatedRegulatoryAgency
            ) {
              if (isValidatedMarketingStatus)
                validatedInfoTemp.push({
                  groupTableKey: "Approval Process Contents",
                  groupTableIndex: 1,
                  tableKey: "Approval Process",
                  tableIndex: 0,
                  groupShellKey: "Approval Process",
                  groupShellIndex: groupShellInfoIndex,
                  shellKey: "Marketing Status",
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });

              if (isValidatedRegulatoryAgency)
                validatedInfoTemp.push({
                  groupTableKey: "Approval Process Contents",
                  groupTableIndex: 1,
                  tableKey: "Approval Process",
                  tableIndex: 0,
                  groupShellKey: "Approval Process",
                  groupShellIndex: groupShellInfoIndex,
                  shellKey: "Regulatory Agency",
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });

              if (isValidatedApplicationType)
                validatedInfoTemp.push({
                  groupTableKey: "Approval Process Contents",
                  groupTableIndex: 1,
                  tableKey: "Approval Process",
                  tableIndex: 0,
                  groupShellKey: "Approval Process",
                  groupShellIndex: groupShellInfoIndex,
                  shellKey: "Application Type",
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });

              if (isValidatedAuthorizedCompany)
                validatedInfoTemp.push({
                  groupTableKey: "Approval Process Contents",
                  groupTableIndex: 1,
                  tableKey: "Approval Process",
                  tableIndex: 0,
                  groupShellKey: "Approval Process",
                  groupShellIndex: groupShellInfoIndex,
                  shellKey: "Authorized Company",
                  shellIndex: 0,
                  valueIndex: null,
                  type: "alert",
                  container: "shell",
                });
            }
          },
        );

        return validatedInfoTemp;
      };

      return sortedValidatedInfo();
    };

    const handleCheckValidationHalf = () => {
      if (checkModalityCategory()?.length === 0) {
        changeInfo(DEFAULT_POSITION_INFO);
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
        return true;
      }

      const validatedList = [...(checkModalityCategory() || [])];

      changeValidatedInfo(validatedList as ValidatedInfoList);
      return false;
    };

    const handleCheckValidationFull = () => {
      if (
        checkModalityCategory()?.length === 0 &&
        checkApprovalProcessTable().length === 0
      ) {
        changeInfo(DEFAULT_POSITION_INFO);
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
        return true;
      }

      const validatedList = [
        ...(checkModalityCategory() || []),
        ...(checkApprovalProcessTable() || []),
      ];

      changeValidatedInfo(validatedList as ValidatedInfoList);
      return false;
    };

    return {
      handleCheckValidationHalf,
      handleCheckValidationFull,
    };
  };
